'use client'

import { useResetPasswordMutation } from '@/hooks/react-query/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { toast } from '../common/toast'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

const formSchema = z
  .object({
    password: z
      .string()
      .min(1, 'Vui lòng nhập mật khẩu')
      .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
    confirmPassword: z.string().min(1, 'Vui lòng nhập lại mật khẩu'),
  })
  .refine(
    (data) => {
      return data.password === data.confirmPassword
    },
    {
      message: 'Mật khẩu không khớp',
      path: ['confirmPassword'],
    }
  )

type Props = {
  forgotCode: string
  onClose?: () => void
  onSuccess?: () => void
  isPage?: boolean
}

export const ResetPasswordForm = ({
  forgotCode,
  onClose,
  onSuccess,
  isPage,
}: Props) => {
  const router = useRouter()

  const { mutateAsync: resetPassword, isPending } = useResetPasswordMutation()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const res = await resetPassword({
        password: values.password,
        forgot_code: forgotCode,
      })

      if (!res.state) {
        toast.message({
          title: 'Thất bại',
          message: res.message || 'Đặt lại mật khẩu thất bại',
        })
        return
      }

      toast.message({
        title: 'Thành công',
        message: 'Đặt lại mật khẩu thành công',
      })
      if (isPage) router.push('/login')
      onClose?.()
      onSuccess?.()
    } catch (error) {
      console.error(error)
      toast.message({
        title: 'Thất bại',
        message:
          (error as Error).message ||
          'Đặt lại mật khẩu thất bại, vui lòng thử lại',
      })
    }
  }

  return (
    <>
      <h3 className='font-criteriacapitalize text-heading-5 mb-4 text-center font-medium tracking-normal text-neutral-900'>
        Đặt lại mật khẩu
      </h3>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <Input
          label='Mật khẩu'
          className='w-full'
          floating
          autoFocus
          type='password'
          error={form.formState.errors?.password}
          {...form.register('password')}
        />

        <Input
          label='Nhập lại mật khẩu'
          className='w-full'
          floating
          type='password'
          error={form.formState.errors?.confirmPassword}
          {...form.register('confirmPassword')}
        />

        <Button
          className='mt-6 w-full normal-case'
          variant='black'
          disabled={!form.formState.isValid}
          loading={isPending}
        >
          Đặt lại mật khẩu
        </Button>
      </form>
    </>
  )
}
